import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, NgForm } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { JsonHierarchyElement } from '@psnb/nwt-api';
import {
  BaseResponseDTO,
  CurrentTypeDTO,
  FuelTankSizeDTO,
  MaintenanceTypeDTO,
  ManufactureTypeDTO,
  ModelTypeDTO,
  StatesEntity,
  StatusTypeDTO,
  TrailerNameIdDTO,
  VoltageTypeDTO
} from '@psnb/psnb-api';
import {
  AuthService,
  GeneratorService,
  GetHierarchyService,
  isDefined,
  PowerSupplyService,
  UserPermission
} from '@psnb/services';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { CommonService } from '../../common/common.service';
import { MessageService } from '../message.service';
import { PowerSupplyMessageDialog } from '../../common/message-dialog/power-supply-message-dialog';
import { GpsValidation } from './gps.validator';
import { PowerSupplyGeneratorCreateViewModel } from './power-supply-generator-create.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'nwt-power-supply-generator-create',
  styleUrls: ['./power-supply-generator-create.component.scss'],
  templateUrl: './power-supply-generator-create.component.html'
})
export class PowerSupplyGeneratorCreateComponent implements OnInit, AfterViewInit, OnDestroy {
  public vm: PowerSupplyGeneratorCreateViewModel;

  // Public ViewChild references
  @ViewChild('fuelTankForm') public fuelTankForm: NgForm;
  @ViewChild('generatorForm') public generatorForm: NgForm;
  @ViewChild('trailerForm') public trailerForm: NgForm;

  // Private ViewChild references
  @ViewChild('focusFrame', { read: ElementRef }) private focusFrameElement?: ElementRef<HTMLElement>;
  @ViewChild('fuelTankScanner', { read: ElementRef }) private fuelTankScannerElement?: ElementRef<HTMLElement>;
  @ViewChild('scanner', { read: ElementRef }) private scannerElement?: ElementRef<HTMLElement>;
  @ViewChild('trailerScanner', { read: ElementRef }) private trailerScannerElement?: ElementRef<HTMLElement>;

  // Character code constants for numeric input validation
  private readonly CONTROL_CHAR_MAX: number = 31;
  private readonly CHAR_CODE_ZERO: number = 48;
  private readonly CHAR_CODE_NINE: number = 57;
  private readonly destroyed$: Subject<void>;

  private activeDialog?: MatDialogRef<PowerSupplyMessageDialog>;
  private activeRequests: number = 0;
  private isComponentInitialized: boolean = false;
  private permissionValue: boolean;
  private runTimeHours: number;
  private selectedDivision: number | undefined = undefined;
  private selectedDivisionName: string = '';
  private selectedManufacturer: number | undefined = undefined;
  private selectedManufacturerName: string = '';
  private selectedModel: number | undefined = undefined;
  private selectedModelName: string = '';
  private selectedRegionName: string = '';
  private selectedStatus: number | undefined = undefined;
  private selectedStatusName: string = '';
  private selectedTrailerName: string = '';
  private userName: string = '';
  private userpermissions: UserPermission[];

  public constructor(
    private authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private commonService: CommonService,
    private data: MessageService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private generatorService: GeneratorService,
    private getHierarchyService: GetHierarchyService,
    private ngZone: NgZone,
    private powerSupplyService: PowerSupplyService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.destroyed$ = new Subject<void>();
  }

  public ngOnInit(): void {
    this.initializeViewModel();
    this.setupRouteSubscriptions();
    this.checkPermissions();
    this.loadAllDropdownData();
    this.initializeLocationForms();
    this.setupCreateSaveSubscription();
  }

  private initializeViewModel(): void {
    this.vm = {
      address: '',
      assetId: '',
      awayFromHome: undefined,
      cdlRequired: undefined,
      city: '',
      comments: '',
      currentGpsLatitude: '',
      currentGpsLongitude: '',
      currentTypes: [],
      divisions: [],
      fuelTankAssetId: '',
      fuelTankCapacity: undefined,
      fuelTankComments: '',
      fuelTankLoanedToTeam: undefined,
      fuelTankLocalId: '',
      fuelTankLocationInfoForm: undefined,
      fuelTankManufacturer: undefined,
      fuelTankModel: undefined,
      fuelTankScannedValue: '',
      fuelTankScannerEnabled: false,
      fuelTankSerialNumber: '',
      fuelTankSizes: [],
      fuelTankTeam: undefined,
      fuelTankZoomFlag: false,
      fuelTankZoomLevel: 1,
      generatorCapacity: '',
      generatorChain: '',
      generatorCord: '',
      generatorLocationInfoForm: undefined,
      generatorLock: '',
      generatorTabIndex: 0,
      hitchSize: '',
      hitchType: '',
      houseNumberInput: '',
      isModelLoading: false,
      isRegionsLoading: false,
      isTeamsLoading: false,
      isTrailerLoading: false,
      licensePlate: '',
      loanedToTeams: [],
      localId: '',
      lockCombination: '',
      lockType: '',
      maintenances: [],
      manufacturers: [],
      models: [],
      outputVoltage: '',
      ownerContact: '',
      primaryContact: '',
      queryParamsData: {},
      regions: [],
      scannedValue: '',
      scannerEnabled: false,
      secondaryContact: '',
      selectedCurrentType: '',
      selectedDivisionObj: undefined,
      selectedLoanedToTeam: undefined,
      selectedMaintenance: undefined,
      selectedManufacturerObj: undefined,
      selectedModelObj: undefined,
      selectedRegion: undefined,
      selectedRegionObj: undefined,
      selectedState: '',
      selectedStatusObj: undefined,
      selectedTeam: undefined,
      selectedTrailer: undefined,
      selectedTrailerObj: undefined,
      selectedVoltage: '',
      serialNumber: '',
      showProgressBar: false,
      states: [],
      statuses: [],
      street: '',
      teams: [],
      trailerHeight: '',
      trailerLocationInfoForm: undefined,
      trailerName: '',
      trailers: [],
      trailerScannedValue: '',
      trailerScannerEnabled: false,
      trailerSize: '',
      trailerZoomFlag: false,
      trailerZoomLevel: 1,
      voltageTypes: [],
      zip: '',
      zipcode: '',
      zoomFlag: false,
      zoomLevel: 1
    };
  }

  private setupRouteSubscriptions(): void {
    this.route.queryParams.subscribe((params: Params): void => {
      this.vm.queryParamsData = params;
      this.cdr.detectChanges();
    });
  }

  private checkPermissions(): void {
    const userPermissionsRaw: string | undefined = localStorage.getItem('user-permissions') ?? undefined;
    this.userpermissions = userPermissionsRaw ? JSON.parse(userPermissionsRaw) : undefined;
    if (!this.authService.isAuthorizedForPowerSupply(this.userpermissions)) {
      this.router.navigate(['/unauthorized']);
      return;
    }

    const userDetails: { permissions: string[] } = JSON.parse(localStorage['user-details']);
    if ((userDetails.permissions.includes('{1091}'))) {
      this.permissionValue = true;
    } else {
      this.permissionValue = false;
    }

    this.userName = `${this.authService.getUserDetails.firstName} ${this.authService.getUserDetails.lastName}`;
  }

  private loadAllDropdownData(): void {
    this.loadStatusTypes();
    this.loadManufactureTypes();
    this.loadCurrentTypes();
    this.loadFuelTankSizes();
    this.loadVoltageTypes();
    this.loadLoanedNWTTeams();
    this.loadDivisions();
    this.loadMaintenanceTypes();
    this.loadStates();
  }

  private setupCreateSaveSubscription(): void {
    this.data.MessageCreateSave
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data: { iscreatesubmit?: boolean }): void => {
        if (!this.isComponentInitialized) {
          return;
        }

        if (data.iscreatesubmit) {
          const assetType: string | undefined = this.vm.queryParamsData?.assetType;
          switch (assetType) {
            case 'Generator':
              this.createGenerator();
              break;
            case 'Trailer':
              this.createTrailer();
              break;
            case 'Fuel Tank':
              this.createFuelTank();
              break;
            default:
              break;
          }
        }
      });
  }

  public ngAfterViewInit(): void {
    // Subscribe to form status changes after Angular stabilizes
    this.ngZone.onStable
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe((): void => {
        // Subscribe to generator form changes
        if (this.generatorForm) {
          this.generatorForm.statusChanges
            ?.pipe(takeUntil(this.destroyed$))
            .subscribe((): void => this.onChangeSave());
        }

        // Subscribe to trailer form changes
        if (this.trailerForm) {
          this.trailerForm.statusChanges
            ?.pipe(takeUntil(this.destroyed$))
            .subscribe((): void => this.onChangeSave());
        }

        // Subscribe to fuel tank form changes
        if (this.fuelTankForm) {
          this.fuelTankForm.statusChanges
            ?.pipe(takeUntil(this.destroyed$))
            .subscribe((): void => this.onChangeSave());
        }

        // Subscribe to location form changes
        if (this.vm.generatorLocationInfoForm) {
          this.vm.generatorLocationInfoForm.statusChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((): void => this.onChangeSave());
        }

        if (this.vm.trailerLocationInfoForm) {
          this.vm.trailerLocationInfoForm.statusChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((): void => this.onChangeSave());
        }

        if (this.vm.fuelTankLocationInfoForm) {
          this.vm.fuelTankLocationInfoForm.statusChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe((): void => this.onChangeSave());
        }

        // Set initial button state
        this.onChangeSave();

        // Mark component as initialized after all setup is complete
        this.isComponentInitialized = true;
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$?.next();
    this.destroyed$?.complete();
  }

  private loadStatusTypes(): void {
    this.startLoading();
    this.generatorService.getStatusTypesService()
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((response: StatusTypeDTO[]): void => {
        this.vm.statuses = response;
        this.cdr.detectChanges();
      });
  }

  private loadManufactureTypes(): void {
    this.startLoading();
    this.generatorService.getManufactureTypesService()
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((response: ManufactureTypeDTO[]): void => {
        this.vm.manufacturers = response;
        this.cdr.detectChanges();
      });
  }

  private loadCurrentTypes(): void {
    this.startLoading();
    this.generatorService.getCurrentTypesService()
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((result: CurrentTypeDTO[]): void => {
        this.vm.currentTypes = result;
        this.cdr.detectChanges();
      });
  }

  private loadFuelTankSizes(): void {
    this.startLoading();
    this.generatorService.getFuelTankSizesService()
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading()))
      .subscribe((fuelTankSizes: FuelTankSizeDTO[]): void => {
        this.vm.fuelTankSizes = fuelTankSizes;
        this.cdr.detectChanges();
      });
  }

  private loadVoltageTypes(): void {
    this.startLoading();
    this.generatorService.getVoltageTypesService()
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((result: VoltageTypeDTO[]): void => {
        this.vm.voltageTypes = result;
        this.cdr.detectChanges();
      });
  }

  private loadLoanedNWTTeams(): void {
    this.startLoading();
    this.generatorService.getAllLoanedNWTTeams()
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((result: string[]): void => {
        this.vm.loanedToTeams = result;
        this.cdr.detectChanges();
      });
  }

  private loadDivisions(): void {
    this.startLoading();
    this.getHierarchyService.getChildren({ elementID: 1 })
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((results: JsonHierarchyElement[]): void => {
        const items: JsonHierarchyElement[] = results ?? [];
        this.vm.divisions = items.filter((result: JsonHierarchyElement): boolean => {
          const name: string = result?.name?.toString().trim().toLowerCase() ?? '';
          return name !== 'national test' && name !== 'national';
        });
        this.cdr.detectChanges();
      });
  }

  private loadMaintenanceTypes(): void {
    this.startLoading();
    this.generatorService.getMaintenanceTypesService()
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((maintenances: MaintenanceTypeDTO[]): void => {
        this.vm.maintenances = maintenances;
        this.cdr.detectChanges();
      });
  }

  private loadStates(): void {
    this.startLoading();
    this.generatorService.getStatesService()
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe((getStates: StatesEntity[]): void => {
        this.vm.states = getStates;
        this.cdr.detectChanges();
      });
  }

  private initializeLocationForms(): void {
    // Initialize trailer location form
    this.vm.trailerLocationInfoForm = this.fb.group({
      currentGpsLatitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLatitudeRange()
        ]
      ],
      currentGpsLongitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLongitudeRange()
        ]
      ]
    });

    this.vm.trailerLocationInfoForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((): void => {
        this.onChangeLocation(this.vm.trailerLocationInfoForm);
      });

    // Initialize generator location form
    this.vm.generatorLocationInfoForm = this.fb.group({
      currentGpsLatitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLatitudeRange()
        ]
      ],
      currentGpsLongitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLongitudeRange()
        ]
      ]
    });

    this.vm.generatorLocationInfoForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((): void => {
        this.onChangeLocation(this.vm.generatorLocationInfoForm);
      });

    // Initialize fuel tank location form
    this.vm.fuelTankLocationInfoForm = this.fb.group({
      currentGpsLatitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLatitudeRange()
        ]
      ],
      currentGpsLongitude: [
        '',
        [
          GpsValidation.ValidateLongitudeLatitudeDecimal(),
          GpsValidation.ValidateLongitudeRange()
        ]
      ]
    });

    this.vm.fuelTankLocationInfoForm.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((): void => {
        this.onChangeLocation(this.vm.fuelTankLocationInfoForm);
      });
  }

  private startLoading(): void {
    this.activeRequests++;
    this.vm.showProgressBar = true;
    this.cdr.detectChanges();
  }

  private stopLoading(): void {
    this.activeRequests--;
    if (this.activeRequests <= 0) {
      this.activeRequests = 0;
      this.vm.showProgressBar = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Opens a message dialog safely, ensuring only one dialog is open at a time.
   *
   * @param {string} message The message to display in the dialog
   * @returns {MatDialogRef<PowerSupplyMessageDialog>} The dialog reference
   */
  private openMessageDialog(message: string): MatDialogRef<PowerSupplyMessageDialog> {
    // Close existing dialog if one is already open
    if (this.activeDialog) {
      this.activeDialog.close();
    }

    // Open new dialog
    this.activeDialog = this.dialog.open(PowerSupplyMessageDialog, {
      data: { message }
    });

    // Clear reference when dialog is closed
    this.activeDialog.afterClosed().pipe(take(1)).subscribe((): void => {
      this.activeDialog = undefined;
    });

    return this.activeDialog;
  }

  /**
   * Handles location form changes and updates GPS coordinates in the view model.
   *
   * @param {FormGroup | undefined} form The location form group containing GPS latitude and longitude values
   */
  public onChangeLocation(form: FormGroup | undefined): void {
    if (isDefined(form)) {
      this.vm.currentGpsLatitude = form.value.currentGpsLatitude;
      this.vm.currentGpsLongitude = form.value.currentGpsLongitude;
    }
    // Trigger button state update when location changes
    this.onChangeSave();
  }

  /**
   * Trims leading and trailing whitespace from a generator field.
   *
   * @param {string} fieldName The name of the field in the view model to trim
   */
  public trimGeneratorLeadingAndTrailingSpaces(fieldName: string): void {
    this.trimField(fieldName);
  }

  private trimField(fieldName: string): void {
    if (isDefined(this.vm[fieldName]) && typeof this.vm[fieldName] === 'string') {
      this.vm[fieldName] = this.vm[fieldName].trim();
      this.cdr.detectChanges();
    }
  }

  /**
   * Trims leading and trailing whitespace from a field in the view model.
   *
   * @param {string} fieldName The name of the field to trim
   */
  public trimSpaces(fieldName: string): void {
    this.trimField(fieldName);
  }

  /**
   * Validates that only numeric characters are entered in an input field.
   *
   * @param {KeyboardEvent} event The keyboard event to validate
   * @returns {boolean} True if the character is numeric, false otherwise
   */
  public numberOnly(event: KeyboardEvent): boolean {
    const charCode: number | undefined = (event.which) ? event.which : event.keyCode;
    if (charCode > this.CONTROL_CHAR_MAX && (charCode < this.CHAR_CODE_ZERO || charCode > this.CHAR_CODE_NINE)) {
      return false;
    }
    return true;
  }

  /**
   * Prevents pasting of non-numeric text into input fields.
   *
   * @param {ClipboardEvent} event The clipboard event containing the pasted data
   */
  public restrictPasteForText(event: ClipboardEvent): void {
    const clipboardData: DataTransfer | undefined = event.clipboardData;
    const pastedText: string | undefined = clipboardData?.getData('text');
    if (isDefined(pastedText) && !/^\d+$/.test(pastedText)) {
      event.preventDefault();
    }
  }

  /**
   * Handles status selection change and updates the selected status values.
   *
   * @param {StatusTypeDTO} status The selected status object
   */
  public onStatusChange(status: StatusTypeDTO): void {
    if (isDefined(status)) {
      this.selectedStatus = status.id ?? undefined;
      this.selectedStatusName = status.statusType ?? '';
    }
  }

  /**
   * Handles manufacturer selection change, updates manufacturer values, and loads associated models.
   *
   * @param {ManufactureTypeDTO} manufacturer The selected manufacturer object
   */
  public onManufacturerChange(manufacturer: ManufactureTypeDTO): void {
    if (isDefined(manufacturer)) {
      this.selectedManufacturer = manufacturer.id ?? undefined;
      this.selectedManufacturerName = manufacturer.manufacture ?? '';
      this.vm.models = [];
      this.vm.selectedModelObj = undefined;
      this.loadModels(this.selectedManufacturerName);
      this.cdr.detectChanges();
    }
  }

  private loadModels(manufacturerName: string): void {
    this.vm.isModelLoading = true;
    this.cdr.detectChanges();

    this.generatorService.getModelsByManufacturer(manufacturerName)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.isModelLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((models: ModelTypeDTO[]): void => {
        this.vm.models = models ?? [];
        this.cdr.detectChanges();
      });
  }

  /**
   * Handles model selection change and updates the selected model values.
   *
   * @param {ModelTypeDTO} model The selected model object
   */
  public onModelChange(model: ModelTypeDTO): void {
    if (isDefined(model)) {
      this.selectedModel = model.id ?? undefined;
      this.selectedModelName = model.genModel ?? '';
    }
  }

  /**
   * Handles division selection change, clears dependent fields, and loads associated regions.
   *
   * @param {JsonHierarchyElement} division The selected division object
   */
  public onDivisionChange(division: JsonHierarchyElement): void {
    if (isDefined(division)) {
      this.selectedDivision = division.elementID;
      this.selectedDivisionName = division.name?.toString() ?? '';
      this.vm.regions = [];
      this.vm.selectedRegionObj = undefined;
      this.vm.teams = [];
      this.vm.selectedTeam = undefined;
      this.loadRegions(division.elementID);
      this.cdr.detectChanges();
    }
  }

  private loadRegions(divisionId: number): void {
    this.vm.isRegionsLoading = true;
    this.cdr.detectChanges();

    this.getHierarchyService.getChildren({ elementID: divisionId })
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.isRegionsLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((results: JsonHierarchyElement[]): void => {
        this.vm.regions = results ?? [];
        this.cdr.detectChanges();
      });
  }

  /**
   * Handles region selection change, clears dependent fields, and loads associated teams.
   *
   * @param {JsonHierarchyElement} region The selected region object
   */
  public onRegionChange(region: JsonHierarchyElement): void {
    if (isDefined(region)) {
      this.vm.selectedRegion = region.elementID;
      this.selectedRegionName = region.name?.toString() ?? '';
      this.vm.teams = [];
      this.vm.selectedTeam = undefined;
      this.vm.trailers = [];
      this.vm.selectedTrailer = undefined;
      this.loadTeams(region.elementID);
      this.cdr.detectChanges();
    }
  }

  private loadTeams(regionId: number): void {
    this.vm.isTeamsLoading = true;
    this.cdr.detectChanges();
    this.startLoading();

    this.powerSupplyService.getNWTTeamsList([regionId])
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.stopLoading();
        })
      )
      .subscribe({
        error: (): void => {
          this.vm.isTeamsLoading = false;
          this.cdr.detectChanges();
        },
        next: (teams: string[]): void => {
          this.vm.teams = teams ?? [];
          this.vm.isTeamsLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Handles team selection change and loads associated trailers if creating a generator or fuel tank.
   *
   * @param {string} team The selected team name
   */
  public onTeamChange(team: string): void {
    if (isDefined(team) &&
        (this.vm.queryParamsData?.assetType === 'Generator' ||
         this.vm.queryParamsData?.assetType === 'Fuel Tank')) {
      // Clear trailer-related state before loading new trailers
      this.vm.trailers = [];
      this.vm.selectedTrailer = undefined;
      this.vm.selectedTrailerObj = undefined;
      this.selectedTrailerName = '';
      this.loadTrailers(team);
    }
  }

  /**
   * Handles trailer selection change.
   *
   * @param {TrailerNameIdDTO} trailer The selected trailer object
   */
  public onTrailerChange(trailer: TrailerNameIdDTO): void {
    if (isDefined(trailer)) {
      this.vm.selectedTrailerObj = trailer;
      this.vm.selectedTrailer = trailer.trailerId ?? undefined;
      this.selectedTrailerName = trailer.trailerName ?? '';
    } else {
      this.vm.selectedTrailerObj = undefined;
      this.vm.selectedTrailer = undefined;
      this.selectedTrailerName = '';
    }
    this.cdr.detectChanges();
  }

  private loadTrailers(teamName: string): void {
    this.vm.isTrailerLoading = true;
    this.cdr.detectChanges();

    this.generatorService.getTrailersByOwnerTeam(teamName)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => {
          this.vm.isTrailerLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe((trailers: TrailerNameIdDTO[]): void => {
        this.vm.trailers = trailers ?? [];
        this.cdr.detectChanges();
      });
  }

  /**
   * Creates a new generator asset with the provided form data.
   * Validates required fields and submits the generator payload to the service.
   */
  public createGenerator(): void {
    if (!this.isGeneratorFormValid()) {
      this.openMessageDialog('Please fill all required fields.');
      return;
    }

    const timeValue: string = new Date().toISOString();

    const generatorPayload: Record<string, unknown> = {
      address: this.vm.address,
      city: this.vm.city,
      comments: this.vm.comments,
      createDate: timeValue,
      createdByUserId: this.userName ? this.userName : undefined,
      currentType: this.vm.selectedCurrentType,
      division: isDefined(this.selectedDivisionName)
        ? String(this.selectedDivisionName)
        : undefined,
      generatorChain: this.vm.generatorChain,
      generatorCord: this.vm.generatorCord,
      generatorLastModified: timeValue,
      generatorLastModifiedBy: this.userName ? this.userName : undefined,
      generatorLock: this.vm.generatorLock,
      generatorManufactureId: this.selectedManufacturer ? Number(this.selectedManufacturer) : undefined,
      generatorManufacturer: this.selectedManufacturerName,
      generatorModel: this.selectedModelName,
      generatorModelId: this.selectedModel ? Number(this.selectedModel) : undefined,
      generatorRuntimeHours: isDefined(this.runTimeHours) ?
        Number(this.runTimeHours) : 0,
      generatorSerialNumber: this.vm.serialNumber,
      generatorStatus: this.selectedStatusName ? String(this.selectedStatusName) : undefined,
      generatorStatusId: this.selectedStatus ? Number(this.selectedStatus) : undefined,
      lastModified: timeValue,
      lastModifiedBy: this.userName ? this.userName : undefined,
      latitude: this.vm.generatorLocationInfoForm?.value.currentGpsLatitude,
      loanedTeamName: isDefined(this.vm.selectedLoanedToTeam)
        ? String(this.vm.selectedLoanedToTeam)
        : undefined,
      localId: this.vm.localId,
      longitude: this.vm.generatorLocationInfoForm?.value.currentGpsLongitude,
      maintenance: isDefined(this.vm.selectedMaintenance)
        ? String(this.vm.selectedMaintenance)
        : undefined,
      outputVoltage: this.vm.selectedVoltage,
      ownerContact: this.vm.ownerContact,
      qrCodeId: this.vm.assetId,
      region: isDefined(this.selectedRegionName) ? String(this.selectedRegionName) : undefined,
      state: this.vm.selectedState,
      teamName: isDefined(this.vm.selectedTeam) ? String(this.vm.selectedTeam) : undefined,
      trailerId: this.vm.selectedTrailer,
      zipcode: this.vm.zipcode
    };

    this.startLoading();
    this.generatorService.createGenerator(generatorPayload)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe({
        error: (): void => {
          this.openMessageDialog('Error creating generator. Please try again.');
        },
        next: (generatorResponse: BaseResponseDTO): void => {
          if (generatorResponse?.success) {
            const successMessage: string = this.vm.assetId
              ? `Generator Created Successfully. QR Code Asset Id: ${this.vm.assetId}`
              : 'Generator Created Successfully.';
            const dialogRef: MatDialogRef<PowerSupplyMessageDialog> = this.openMessageDialog(successMessage);
            dialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
              if (generatorResponse?.success) {
                this.router.navigate(['/generatorsearch']);
              }
            });
          } else if (!generatorResponse?.success) {
            let errorMessage: string = generatorResponse?.message ?? 'Error creating generator. Please try again.';
            if (isDefined(errorMessage) && typeof errorMessage === 'string') {
              errorMessage = errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
            }
            const dialogRef: MatDialogRef<PowerSupplyMessageDialog> = this.openMessageDialog(errorMessage);
            dialogRef.afterClosed().pipe(take(1)).subscribe();
          }
        }
      });
  }

  /**
   * Creates a new trailer asset with the provided form data.
   * Validates required fields and submits the trailer payload to the service.
   */
  public createTrailer(): void {
    if (!this.trailerForm?.form?.valid || !this.isTrailerFormValid()) {
      if (this.trailerForm?.form?.markAllAsTouched) {
        this.trailerForm.form.markAllAsTouched();
      }
      if (this.vm.trailerLocationInfoForm) {
        Object.values(this.vm.trailerLocationInfoForm.controls).forEach(
          (createTrailer: { markAsTouched: () => void; markAsDirty: () => void }): void => {
            createTrailer.markAsTouched();
            createTrailer.markAsDirty();
          }
        );
      }
      this.openMessageDialog('Please fill all required fields for Trailer.');
      return;
    }

    const loggedHours: string = new Date().toISOString();

    const trailerPayload: Record<string, unknown> = {
      awayFromHome: (this.vm.awayFromHome === 1 || this.vm.awayFromHome === 0) ? this.vm.awayFromHome : undefined,
      cdlRequired: (this.vm.cdlRequired === 1 || this.vm.cdlRequired === 0) ? this.vm.cdlRequired : undefined,
      city: this.vm.city,
      comments: this.vm.comments,
      currentLatitude: this.vm.currentGpsLatitude,
      currentLongitude: this.vm.currentGpsLongitude,
      division: this.selectedDivisionName,
      generatorCapacity: this.vm.generatorCapacity,
      hitchSize: this.vm.hitchSize,
      hitchType: this.vm.hitchType,
      houseNumber: this.vm.houseNumberInput,
      licensePlate: this.vm.licensePlate,
      localId: this.vm.localId,
      lockCombination: this.vm.lockCombination,
      lockType: this.vm.lockType,
      primaryContact: this.vm.primaryContact,
      qrCodeId: this.vm.assetId,
      region: this.selectedRegionName,
      secondaryContact: this.vm.secondaryContact,
      serialNumber: this.vm.serialNumber,
      state: this.vm.selectedState,
      status: this.selectedStatusName ?? (this.vm.selectedStatusObj?.statusType ?? undefined),
      street: this.vm.street,
      teamName: isDefined(this.vm.selectedTeam) ?
        String(this.vm.selectedTeam) : undefined,
      trailerCreateDate: loggedHours,
      trailerCreatedByUserId: this.userName ? this.userName : undefined,
      trailerHeight: this.vm.trailerHeight,
      trailerLastModifiedByUserId: this.userName ? this.userName : undefined,
      trailerLastModifiedDate: loggedHours,
      trailerLoanedTeamId: isDefined(this.vm.selectedLoanedToTeam) ?
        String(this.vm.selectedLoanedToTeam) : undefined,
      trailerName: this.vm.trailerName,
      trailerSize: this.vm.trailerSize,
      zipCode: this.vm.zip
    };

    this.startLoading();
    this.generatorService.createTrailer(trailerPayload)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe({
        error: (): void => {
          this.openMessageDialog('Error creating trailer. Please try again.');
        },
        next: (response: BaseResponseDTO): void => {
          if (response?.success) {
            const successMessage: string = this.vm.assetId
              ? `Trailer Created Successfully. QR Code Asset Id: ${this.vm.assetId}`
              : 'Trailer Created Successfully.';
            const dialogRef: MatDialogRef<PowerSupplyMessageDialog> = this.openMessageDialog(successMessage);
            dialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
              if (response?.success) {
                this.router.navigate(['/generatorsearch']);
              }
            });
          } else {
            const errorMessage: string = response?.message ?? 'Error creating trailer. Please try again.';
            this.openMessageDialog(errorMessage);
          }
        }
      });
  }

  /**
   * Creates a new fuel tank asset with the provided form data.
   * Validates required fields and submits the fuel tank payload to the service.
   */
  public createFuelTank(): void {
    if (!this.isFuelTankFormValid()) {
      this.openMessageDialog('Please fill all required fields for Fuel Tank.');
      return;
    }

    const loggedHours: string = new Date().toISOString();

    const fuelTankPayload: Record<string, unknown> = {
      address: this.vm.address,
      capacity: this.vm.fuelTankCapacity,
      city: this.vm.city,
      comments: this.vm.fuelTankComments,
      createDate: loggedHours,
      createdByUserId: this.userName ? this.userName : undefined,
      division: this.selectedDivisionName,
      lastModifiedByUserId: this.userName ? this.userName : undefined,
      lastModifiedDate: loggedHours,
      latitude: this.vm.fuelTankLocationInfoForm?.value.currentGpsLatitude,
      loanedToTeam: isDefined(this.vm.fuelTankLoanedToTeam)
        ? String(this.vm.fuelTankLoanedToTeam)
        : undefined,
      localId: this.vm.fuelTankLocalId,
      longitude: this.vm.fuelTankLocationInfoForm?.value.currentGpsLongitude,
      qrCodeId: this.vm.fuelTankAssetId,
      region: this.selectedRegionName,
      serialNumber: this.vm.fuelTankSerialNumber,
      state: this.vm.selectedState,
      status: this.selectedStatusName,
      teamName: isDefined(this.vm.selectedTeam)
        ? String(this.vm.selectedTeam)
        : undefined,
      trailerId: isDefined(this.vm.selectedTrailer)
        ? Number(this.vm.selectedTrailer)
        : undefined,
      trailerName: this.selectedTrailerName,
      zipCode: this.vm.zipcode
    };

    this.startLoading();
    this.generatorService.createFuelTank(fuelTankPayload)
      .pipe(
        takeUntil(this.destroyed$),
        finalize((): void => this.stopLoading())
      )
      .subscribe({
        error: (): void => {
          this.openMessageDialog('Error creating fuel tank. Please try again.');
        },
        next: (response: BaseResponseDTO): void => {
          if (response?.success) {
            const successMessage: string = this.vm.fuelTankAssetId
              ? `Fuel Tank Created Successfully. QR Code Asset Id: ${this.vm.fuelTankAssetId}`
              : 'Fuel Tank Created Successfully.';
            const dialogRef: MatDialogRef<PowerSupplyMessageDialog> = this.openMessageDialog(successMessage);
            dialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
              if (response?.success) {
                this.router.navigate(['/generatorsearch']);
              }
            });
          } else {
            const errorMessage: string = response?.message ?? 'Error creating fuel tank. Please try again.';
            this.openMessageDialog(errorMessage);
          }
        }
      });
  }

  private isGeneratorFormValid(): boolean {
    return Boolean(
      this.selectedStatus &&
      this.vm.serialNumber &&
      this.selectedManufacturer &&
      this.selectedModel &&
      this.vm.selectedCurrentType &&
      this.vm.selectedVoltage &&
      this.selectedDivisionName &&
      this.selectedRegionName &&
      this.vm.selectedTeam
    );
  }

  private isTrailerFormValid(): boolean {
    const cdlSelected: boolean = (this.vm.cdlRequired === 1 || this.vm.cdlRequired === 0);
    return Boolean(
      this.selectedStatus &&
      this.vm.trailerSize &&
      this.vm.trailerHeight &&
      this.vm.selectedTeam &&
      this.selectedDivisionName &&
      this.selectedRegionName &&
      this.vm.houseNumberInput &&
      this.vm.street &&
      this.vm.city &&
      this.vm.selectedState &&
      this.vm.zip &&
      this.vm.licensePlate &&
      cdlSelected &&
      this.vm.hitchSize &&
      this.vm.hitchType &&
      this.vm.primaryContact &&
      this.vm.lockType &&
      this.vm.lockCombination
    );
  }

  private isFuelTankFormValid(): boolean {
    return Boolean(
      this.vm.fuelTankCapacity &&
      this.selectedDivisionName &&
      this.selectedRegionName &&
      this.selectedStatus &&
      this.vm.selectedTeam
    );
  }

  /**
   * Updates the create button state based on form validation status.
   * Checks validity of the active form (generator, trailer, or fuel tank) and enables/disables the button accordingly.
   */
  public onChangeSave(): void {
    let isFormValid: boolean = false;

    const assetType: string | undefined = this.vm.queryParamsData?.assetType;
    switch (assetType) {
      case 'Generator': {
        const formValid: boolean = this.generatorForm?.form?.valid ?? false;
        const locationValid: boolean = !this.vm.generatorLocationInfoForm || this.vm.generatorLocationInfoForm.valid;
        isFormValid = formValid && locationValid;
        break;
      }
      case 'Trailer': {
        const formValid: boolean = this.trailerForm?.form?.valid ?? false;
        const locationValid: boolean = !this.vm.trailerLocationInfoForm || this.vm.trailerLocationInfoForm.valid;
        isFormValid = formValid && locationValid;
        break;
      }
      case 'Fuel Tank': {
        const formValid: boolean = this.fuelTankForm?.form?.valid ?? false;
        const locationValid: boolean = !this.vm.fuelTankLocationInfoForm || this.vm.fuelTankLocationInfoForm.valid;
        isFormValid = formValid && locationValid;
        break;
      }
      default:
        isFormValid = false;
        break;
    }

    // Enable/disable the create button
    // Pass boolean: true = disable button, false = enable button
    if (isFormValid) {
      this.data.createsave('createlocform', false);
    } else {
      this.data.createsave('createlocform', true);
    }
  }

  // TrackBy functions for performance
  /**
   * TrackBy function for status list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {StatusTypeDTO} item The status item
   * @returns {number} The unique identifier for the status item
   */
  public trackByStatus(index: number, item: StatusTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for manufacturer list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {ManufactureTypeDTO} item The manufacturer item
   * @returns {number} The unique identifier for the manufacturer item
   */
  public trackByManufacturer(index: number, item: ManufactureTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for model list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {ModelTypeDTO} item The model item
   * @returns {number} The unique identifier for the model item
   */
  public trackByModel(index: number, item: ModelTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for current type list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {CurrentTypeDTO} item The current type item
   * @returns {number | string} The unique identifier for the current type item
   */
  public trackByCurrentType(index: number, item: CurrentTypeDTO): number | string {
    return item.currentType ?? index;
  }

  /**
   * TrackBy function for voltage type list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {VoltageTypeDTO} item The voltage type item
   * @returns {number} The unique identifier for the voltage type item
   */
  public trackByVoltageType(index: number, item: VoltageTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for division list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {JsonHierarchyElement} item The division item
   * @returns {number} The unique identifier for the division item
   */
  public trackByDivision(index: number, item: JsonHierarchyElement): number {
    return item.elementID ?? index;
  }

  /**
   * TrackBy function for region list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {JsonHierarchyElement} item The region item
   * @returns {number} The unique identifier for the region item
   */
  public trackByRegion(index: number, item: JsonHierarchyElement): number {
    return item.elementID ?? index;
  }

  /**
   * TrackBy function for team list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @returns {number} The index as the unique identifier
   */
  public trackByTeam(index: number): number {
    return index;
  }

  /**
   * TrackBy function for trailer list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {TrailerNameIdDTO} item The trailer item
   * @returns {number} The unique identifier for the trailer item
   */
  public trackByTrailerId(index: number, item: TrailerNameIdDTO): number {
    return item.trailerId;
  }

  /**
   * TrackBy function for state list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {StatesEntity} item The state item
   * @returns {number | string} The unique identifier for the state item
   */
  public trackByState(index: number, item: StatesEntity): number | string {
    return item.name ?? index;
  }

  /**
   * TrackBy function for maintenance list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {MaintenanceTypeDTO} item The maintenance item
   * @returns {number} The unique identifier for the maintenance item
   */
  public trackByMaintenance(index: number, item: MaintenanceTypeDTO): number {
    return item.id ?? index;
  }

  /**
   * TrackBy function for fuel tank size list to optimize rendering performance.
   *
   * @param {number} index The index of the item in the list
   * @param {FuelTankSizeDTO} item The fuel tank size item
   * @returns {number} The unique identifier for the fuel tank size item
   */
  public trackByFuelTankSize(index: number, item: FuelTankSizeDTO): number {
    return item.id ?? index;
  }

  // Generator Scanner Methods
  /**
   * Toggles the QR code scanner for generator asset ID.
   * Enables or disables the scanner and initializes zoom settings.
   */
  public enableScanner(): void {
    if (this.vm.scannerEnabled) {
      this.vm.zoomFlag = false;
      this.vm.scannerEnabled = false;
    } else {
      this.vm.scannedValue = '';
      this.vm.zoomLevel = 1;
      this.vm.zoomFlag = true;
      this.vm.scannerEnabled = true;
    }
    this.cdr.detectChanges();
  }

  /**
   * Handles successful QR code scan for generator asset ID.
   *
   * @param {string} event The scanned value from the QR code
   */
  public scanSuccessHandler(event: string): void {
    this.vm.scannedValue = event;
    this.vm.scannerEnabled = false;
    this.vm.zoomFlag = false;
    this.vm.assetId = event;
    this.cdr.detectChanges();
  }

  /**
   * Increases the zoom level of the generator scanner camera.
   * Maximum zoom level is 1.4x.
   */
  public zoomIn(): void {
    if (this.vm.zoomLevel < 1.4) {
      this.vm.zoomLevel = Math.round((this.vm.zoomLevel + 0.1) * 10) / 10;
      this.applyZoom();
    }
  }

  /**
   * Decreases the zoom level of the generator scanner camera.
   * Minimum zoom level is 1.0x.
   */
  public zoomOut(): void {
    if (this.vm.zoomLevel > 1) {
      this.vm.zoomLevel = Math.round((this.vm.zoomLevel - 0.1) * 10) / 10;
      this.applyZoom();
    }
  }

  private applyZoom(): void {
    if (isDefined(this.scannerElement?.nativeElement)) {
      this.scannerElement.nativeElement.style.transform = `scale(${this.vm.zoomLevel})`;
    }
    if (isDefined(this.focusFrameElement?.nativeElement)) {
      this.focusFrameElement.nativeElement.style.transform = `translate(-50%, -50%) scale(${this.vm.zoomLevel})`;
    }
  }

  // Trailer Scanner Methods
  /**
   * Toggles the QR code scanner for trailer asset ID.
   * Enables or disables the scanner and initializes zoom settings.
   */
  public enableTrailerScanner(): void {
    if (this.vm.trailerScannerEnabled) {
      this.vm.trailerZoomFlag = false;
      this.vm.trailerScannerEnabled = false;
    } else {
      this.vm.trailerScannedValue = '';
      this.vm.trailerZoomLevel = 1;
      this.vm.trailerZoomFlag = true;
      this.vm.trailerScannerEnabled = true;
    }
    this.cdr.detectChanges();
  }

  /**
   * Handles successful QR code scan for trailer asset ID.
   *
   * @param {string} event The scanned value from the QR code
   */
  public trailerScanSuccessHandler(event: string): void {
    this.vm.trailerScannedValue = event;
    this.vm.trailerScannerEnabled = false;
    this.vm.trailerZoomFlag = false;
    this.vm.assetId = event;
    this.cdr.detectChanges();
  }

  /**
   * Increases the zoom level of the trailer scanner camera.
   * Maximum zoom level is 1.4x.
   */
  public trailerZoomIn(): void {
    if (this.vm.trailerZoomLevel < 1.4) {
      this.vm.trailerZoomLevel = Math.round((this.vm.trailerZoomLevel + 0.1) * 10) / 10;
      this.applyTrailerZoom();
    }
  }

  /**
   * Decreases the zoom level of the trailer scanner camera.
   * Minimum zoom level is 1.0x.
   */
  public trailerZoomOut(): void {
    if (this.vm.trailerZoomLevel > 1) {
      this.vm.trailerZoomLevel = Math.round((this.vm.trailerZoomLevel - 0.1) * 10) / 10;
      this.applyTrailerZoom();
    }
  }

  private applyTrailerZoom(): void {
    if (isDefined(this.trailerScannerElement?.nativeElement)) {
      this.trailerScannerElement.nativeElement.style.transform = `scale(${this.vm.trailerZoomLevel})`;
    }
    if (isDefined(this.focusFrameElement?.nativeElement)) {
      this.focusFrameElement.nativeElement.style.transform = `translate(-50%, -50%) scale(${this.vm.trailerZoomLevel})`;
    }
  }

  // Fuel Tank Scanner Methods
  /**
   * Toggles the QR code scanner for fuel tank asset ID.
   * Enables or disables the scanner and initializes zoom settings.
   */
  public enableFuelTankScanner(): void {
    if (this.vm.fuelTankScannerEnabled) {
      this.vm.fuelTankZoomFlag = false;
      this.vm.fuelTankScannerEnabled = false;
    } else {
      this.vm.fuelTankScannedValue = '';
      this.vm.fuelTankZoomLevel = 1;
      this.vm.fuelTankZoomFlag = true;
      this.vm.fuelTankScannerEnabled = true;
    }
    this.cdr.detectChanges();
  }

  /**
   * Handles successful QR code scan for fuel tank asset ID.
   *
   * @param {string} event The scanned value from the QR code
   */
  public fuelTankScanSuccessHandler(event: string): void {
    this.vm.fuelTankScannedValue = event;
    this.vm.fuelTankScannerEnabled = false;
    this.vm.fuelTankZoomFlag = false;
    this.vm.fuelTankAssetId = event;
    this.cdr.detectChanges();
  }

  /**
   * Increases the zoom level of the fuel tank scanner camera.
   * Maximum zoom level is 1.4x.
   */
  public fuelTankZoomIn(): void {
    if (this.vm.fuelTankZoomLevel < 1.4) {
      this.vm.fuelTankZoomLevel = Math.round((this.vm.fuelTankZoomLevel + 0.1) * 10) / 10;
      this.applyFuelTankZoom();
    }
  }

  /**
   * Decreases the zoom level of the fuel tank scanner camera.
   * Minimum zoom level is 1.0x.
   */
  public fuelTankZoomOut(): void {
    if (this.vm.fuelTankZoomLevel > 1) {
      this.vm.fuelTankZoomLevel = Math.round((this.vm.fuelTankZoomLevel - 0.1) * 10) / 10;
      this.applyFuelTankZoom();
    }
  }

  private applyFuelTankZoom(): void {
    if (isDefined(this.fuelTankScannerElement?.nativeElement)) {
      this.fuelTankScannerElement.nativeElement.style.transform = `scale(${this.vm.fuelTankZoomLevel})`;
    }
    if (isDefined(this.focusFrameElement?.nativeElement)) {
      this.focusFrameElement.nativeElement.style.transform = `translate(-50%, -50%) scale(${this.vm.fuelTankZoomLevel})`;
    }
  }
}
